import { getAuctionEditUrl } from 'utils/auction';
import { isAccountStatusReadOnly, vacancyStatisticsHHLink } from 'utils/clickme';
import { ChangeHistoryType } from 'utils/constants/admin';
import { CampaignProductType } from 'utils/constants/campaign';

import { ButtonStyle } from '@hh.ru/magritte-ui';
import { IconWrapperComponentSize24 } from '@hh.ru/magritte-ui-icon/types';
import {
    ArchiveboxOutlinedSize24,
    ChartBarOutlinedSize24,
    ChartLineOutlinedSize24,
    ClockRewindOutlinedSize24,
    CopyOutlinedSize24,
    DocumentOutlinedSize24,
    ImageOutlinedSize24,
    IngotsOutlinedSize24,
    PauseOutlinedSize24,
    PenOutlinedSize24,
    PlayOutlinedSize24,
    PlusCircleOutlinedSize24,
    PlusOutlinedSize24,
} from '@hh.ru/magritte-ui/icon';

import { useCampaignSwitchChecked } from 'components/CampaignsDashboard/hooks/useCampaignSwitchChecked';

import { fetchChangeHistoryEvents } from 'components/CampaignAdminActions/MenuItems/fetchChangeHistoryEvents';
import { fetchRemainderBudgets } from 'components/CampaignAdminActions/RemainderBudgetsModal/fetchRemainderBudgets';
import { fetchSourceAccounts } from 'components/CampaignAdminActions/SourceAccountsModal/fetchSourceAccounts';
import {
    getIsActiveStatus,
    getIsInactiveStatus,
    getIsMutatingActionAvailable,
    getUrlToBanners,
} from 'components/CampaignsDashboard/utils/utils';

import {
    CampaignFrontendStatus,
    DashboardCampaign,
    setArchiveCampaignAlert,
    setCampaignIdsForBudgetIncrease,
    setIsBudgetIncreaseModalVisible,
    setStopCampaignAlert,
} from 'store/models/campaignsDashboard';
import { currentAccount, SessionType } from 'store/models/user';
import { useDispatch, useSelector } from 'store/utils';

export enum CampaignActionKey {
    IncreaseBudget = 'increaseBudget',
    Edit = 'edit',
    AddCreative = 'addCreative',
    GoToCreatives = 'goToCreatives',
    Duplicate = 'duplicate',
    VacancyStatistics = 'vacancyStatistics',
    CampaignStatistics = 'campaignStatistics',
    Pause = 'pause',
    Start = 'start',
    Archive = 'archive',
    ChangeHistory = 'changeHistory',
    RemainderBudgets = 'remainderBudgets',
    SourceAccounts = 'sourceAccounts',
}

export interface CampaignActionsMenuItem {
    key: CampaignActionKey;
    trlKey: string;
    icon: IconWrapperComponentSize24;
    visible?: boolean;
    disabled?: boolean;
    to?: string;
    buttonStyle?: ButtonStyle;
    onClick?: () => void;
}

type UseCampaignActionsMenuItems = (
    campaign: DashboardCampaign,
    onToggleStatus: () => void,
    isStatusToggleLoading: boolean
) => {
    isSudo: boolean;
    actionsMenuItems: CampaignActionsMenuItem[];
    sudoActionsMenuItems: CampaignActionsMenuItem[];
    isMutatingActionAvailable: boolean;
};

export const useCampaignActionsMenuItems: UseCampaignActionsMenuItems = (
    campaign,
    onToggleStatus,
    isStatusToggleLoading
) => {
    const { id: campaignId, productType, frontendStatus, vacancyId, banners, budget } = campaign;

    const sessionType = useSelector((state) => state.user.sessionType);
    const currentAccountStatus = useSelector(currentAccount)?.status;

    const dispatch = useDispatch();

    const isSwitchChecked = useCampaignSwitchChecked(campaign);

    const goToBannersLink = getUrlToBanners(campaignId);

    const isCpc = productType === CampaignProductType.Cpc;
    const isSudo = sessionType === SessionType.Sudo;
    const isActiveStatus = getIsActiveStatus(frontendStatus);
    const isInactiveStatus = getIsInactiveStatus(frontendStatus);
    // Мутирующие действия недоступны в архиве и для read-only аккаунта
    const isMutatingActionAvailable = getIsMutatingActionAvailable(frontendStatus, currentAccountStatus);

    const editLink = {
        [CampaignProductType.Cpc]: `/new/campaign/${campaignId}`,
        [CampaignProductType.SearchAuction]: getAuctionEditUrl(vacancyId),
        // exp_PORTFOLIO-58018_next_line
        [CampaignProductType.Vr]: '',
        // exp_PORTFOLIO-61587_next_line
        [CampaignProductType.VacancyOfTheDay]: '',
    };

    const actionsMenuItems: CampaignActionsMenuItem[] = [
        {
            key: CampaignActionKey.IncreaseBudget,
            trlKey: 'CampaignsDashboard.CampaignsTable.CampaignActions.increaseBudget',
            icon: PlusCircleOutlinedSize24,
            visible: isMutatingActionAvailable,
            disabled: budget === null || frontendStatus === CampaignFrontendStatus.InactiveDueToEndDate,
            onClick: () => {
                dispatch([
                    setIsBudgetIncreaseModalVisible(true),
                    setCampaignIdsForBudgetIncrease(new Set([campaignId])),
                ]);
            },
        },
        {
            key: CampaignActionKey.Edit,
            trlKey: 'CampaignsDashboard.CampaignsTable.CampaignActions.edit',
            icon: PenOutlinedSize24,
            visible: isMutatingActionAvailable,
            to: editLink[productType],
        },
        {
            key: CampaignActionKey.AddCreative,
            trlKey: 'CampaignsDashboard.CampaignsTable.CampaignActions.addCreative',
            icon: PlusOutlinedSize24,
            visible: isCpc && isMutatingActionAvailable,
            to: `/new/campaign/${campaignId}/banner/`,
        },
        {
            key: CampaignActionKey.GoToCreatives,
            trlKey: 'CampaignsDashboard.CampaignsTable.CampaignActions.goToCreatives',
            icon: ImageOutlinedSize24,
            visible: isCpc,
            to: goToBannersLink,
        },
        {
            key: CampaignActionKey.Duplicate,
            trlKey: 'CampaignsDashboard.CampaignsTable.CampaignActions.duplicate',
            icon: CopyOutlinedSize24,
            visible: isCpc && !isAccountStatusReadOnly(currentAccountStatus),
            to: `/new/campaign/${campaignId}/banner/${banners?.[0].id}/copy`,
        },
        {
            key: CampaignActionKey.VacancyStatistics,
            trlKey: 'CampaignsDashboard.CampaignsTable.CampaignActions.vacancyStatistics',
            icon: ChartBarOutlinedSize24,
            visible: true,
            to: vacancyStatisticsHHLink(vacancyId),
        },
        {
            key: CampaignActionKey.CampaignStatistics,
            trlKey: 'CampaignsDashboard.CampaignsTable.CampaignActions.campaignStatistics',
            icon: ChartLineOutlinedSize24,
            visible: true,
            to: `/new/campaign/${campaignId}/statistics/`,
        },
        {
            key: CampaignActionKey.Pause,
            trlKey: 'CampaignsDashboard.CampaignsTable.CampaignActions.pause',
            icon: PauseOutlinedSize24,
            visible: isMutatingActionAvailable && (isActiveStatus || isInactiveStatus),
            onClick: () => {
                if (isStatusToggleLoading || frontendStatus === CampaignFrontendStatus.Unknown) return;
                void dispatch(setStopCampaignAlert({ isVisible: true, campaignIds: new Set([campaignId]) }));
            },
        },
        {
            key: CampaignActionKey.Start,
            trlKey: 'CampaignsDashboard.CampaignsTable.CampaignActions.start',
            icon: PlayOutlinedSize24,
            visible: isMutatingActionAvailable && !isSwitchChecked,
            onClick: () => {
                onToggleStatus();
            },
        },
        {
            key: CampaignActionKey.Archive,
            trlKey: 'CampaignsDashboard.CampaignsTable.CampaignActions.archive',
            icon: ArchiveboxOutlinedSize24,
            visible: isMutatingActionAvailable,
            buttonStyle: 'negative',
            onClick: () => {
                if (isStatusToggleLoading || frontendStatus === CampaignFrontendStatus.Unknown) return;
                void dispatch(setArchiveCampaignAlert({ isVisible: true, campaignId }));
            },
        },
    ];

    const sudoActionsMenuItems: CampaignActionsMenuItem[] = [
        {
            key: CampaignActionKey.ChangeHistory,
            trlKey: 'CampaignsDashboard.CampaignsTable.CampaignActions.changeHistory',
            icon: ClockRewindOutlinedSize24,
            onClick: () => {
                void dispatch(fetchChangeHistoryEvents(ChangeHistoryType.Campaign, campaignId));
            },
        },
        {
            key: CampaignActionKey.RemainderBudgets,
            trlKey: 'CampaignsDashboard.CampaignsTable.CampaignActions.remainderBudgets',
            icon: IngotsOutlinedSize24,
            onClick: () => {
                void dispatch(fetchRemainderBudgets(campaignId));
            },
        },
        {
            key: CampaignActionKey.SourceAccounts,
            trlKey: 'CampaignsDashboard.CampaignsTable.CampaignActions.sourceAccounts',
            icon: DocumentOutlinedSize24,
            onClick: () => {
                void dispatch(fetchSourceAccounts(campaignId));
            },
        },
    ];

    return {
        isSudo,
        actionsMenuItems,
        sudoActionsMenuItems,
        isMutatingActionAvailable,
    };
};
